import { Suspense } from "react";
import { SidebarProvider, useSidebar } from "../context/SidebarContext";
import { useEdition } from "../context/EditionContext";
import { useLocation } from "react-router";
import AppHeader from "./AppHeader";
import Backdrop from "./Backdrop";
import AppSidebar from "./AppSidebar";
import TenantViewBanner from "./TenantViewBanner";
import NewPlansBanner from "./NewPlansBanner";
import SampleDataBanner from "./SampleDataBanner";
import WhatsNewToast from "../components/whats-new/WhatsNewToast";
import WhatsNewModal from "../components/whats-new/WhatsNewModal";
import NewSmsToastStack from "../components/inbound/NewSmsToastStack";
import ChildStoreMenuBoundary from "../components/auth/ChildStoreMenuBoundary";
import { useUrlModal } from "../hooks/useModal";
import { lazyWithRetry } from "../lib/lazyWithRetry";

const SmsConversationModal = lazyWithRetry(
  () => import("../pages/Admin/SmsConversationModal"),
);
const CONVERSATION_MODAL_PARAMS = ["messageId"] as const;

// 넓은 표를 콘텐츠 영역 끝까지 채워야 하는 화면들 — 중앙 정렬·최대폭 캡을 끈다.
// (기본은 max-w-2xl 로 가운데 정렬해 읽기 좋은 폭을 유지하지만, 리베이트 합산 매트릭스처럼
//  통신사×요금제군×업체를 가로로 길게 펼치는 표는 그 캡 때문에 좌우 여백이 생긴다.)
const FULL_WIDTH_ROUTES = [
  "/policy/rebate-matrix",
  "/policy/rebate-matrix-full",
  "/policy/review/face",
  "/policy/quote-sheet",
  "/policy/quote-sheet-builder",
  "/policy/quote-templates",
];

// 넓은 화면에서 화면 높이를 꽉 채우고, 페이지 자체(바깥) 스크롤 대신 안쪽 영역만 스크롤시키는 화면들.
// (구두 검토 그리드처럼 표가 길어 이중 스크롤이 생기는 화면 — 그리드만 스크롤되게 한다.)
const FULL_HEIGHT_ROUTES = ["/policy/review/verbal"];

const LayoutContent: React.FC = () => {
  const { isExpanded, isMobileOpen } = useSidebar();
  const { edition } = useEdition();
  const external = edition === "partial";
  const { pathname } = useLocation();
  const conversationModal = useUrlModal("sms-conversation", CONVERSATION_MODAL_PARAMS);
  const conversationAnchor = conversationModal.params.messageId;
  const validConversationAnchor =
    conversationAnchor && /^(?:(?:inbound|scheduled):)?\d+$/.test(conversationAnchor)
      ? conversationAnchor
      : null;
  // 수신 메시지 화면에는 목록 갱신까지 묶인 기존 호스트가 있다. 그 밖의 화면에서만 이 전역
  // 호스트가 같은 대화창을 맡아 중복 렌더링 없이 보던 화면 위에 띄운다(티켓 #795).
  const hostsGlobalSmsConversation = !pathname.startsWith("/admin/inbound-messages");
  const fullWidth = FULL_WIDTH_ROUTES.some((p) => pathname.startsWith(p));
  const fullHeight = FULL_HEIGHT_ROUTES.some((p) => pathname.startsWith(p));

  return (
    <div className={`min-h-screen xl:flex ${fullHeight ? "xl:h-screen xl:overflow-hidden" : ""}`}>
      <AppSidebar />
      <Backdrop />
      <div
        className={`flex-1 min-w-0 transition-all duration-300 ease-in-out ${
          // 메뉴가 차지하는 폭만큼만 비운다. 기본은 펼친 폭(290px)이고, ⚙️ 설정에서 고정을
          // 풀면 아이콘 폭(90px). 마우스를 올려 잠깐 펼쳐진 상태는 **여기에 반영하지 않는다** —
          // 그건 본문 위에 겹쳐 뜨는 미리보기라 본문이 따라 움직이면 글자가 출렁인다.
          isExpanded ? "xl:ml-[290px]" : "xl:ml-[90px]"
        } ${isMobileOpen ? "ml-0" : ""} ${
          fullHeight ? "xl:h-screen xl:flex xl:flex-col xl:overflow-hidden" : ""
        }`}
      >
        <TenantViewBanner />
        {/* 「새로 들어온 요금제」 띠 — 운영사에게만, 화면을 열 때 한 번만 물어본다(티켓 #862).
            선택 이용(외부용) 화면에는 이 표 자체가 없으므로 띄우지 않는다. */}
        {!external && <NewPlansBanner />}
        {/* 「체험용 테스트 데이터 · 한 번에 지우기」 띠 — 매장대표에게만, 남아 있을 때만. */}
        {!external && <SampleDataBanner />}
        <AppHeader />
        <div
          className={
            fullHeight
              ? "p-4 md:p-6 xl:flex-1 xl:min-h-0 xl:flex xl:flex-col xl:overflow-hidden"
              : fullWidth
                ? "p-4 md:p-6"
                : "p-4 mx-auto max-w-(--breakpoint-2xl) md:p-6"
          }
        >
          <ChildStoreMenuBoundary />
        </div>
      </div>
      {/* 쓰는 도중에 배포가 나가면 여기서 한 줄 알려준다(처음 들어온 순간엔 뜨지 않는다) */}
      <WhatsNewToast />
      {/* 소식 팝업은 부르는 자리(머리말 단추·알림)와 떨어져 여기 산다 — 그래야 어느 쪽에서
          열어도 뜨고, 단추를 머리말에서 빼도 알림의 「확인하기」가 같이 죽지 않는다. */}
      <WhatsNewModal />
      {!external &&
        hostsGlobalSmsConversation &&
        conversationModal.isOpen &&
        validConversationAnchor != null && (
        <Suspense
          fallback={
            <div className="fixed inset-0 z-99999 flex items-center justify-center bg-black/25 text-sm font-semibold text-white">
              문자 대화창을 불러오는 중…
            </div>
          }
        >
          <SmsConversationModal
            isOpen
            conversationAnchor={validConversationAnchor}
            onClose={conversationModal.close}
          />
        </Suspense>
      )}
      {/* 새 문자 알림 — 선택 이용(외부용) 화면에는 띄우지 않는다(2026-08-31). 그 계정에는
          문자 기능 자체가 열려 있지 않아(서버도 같은 카탈로그로 API 를 막는다) 「다른 창을
          봐도 문자 알림 받기」 권유가 없는 기능을 있는 것처럼 광고하는 꼴이었다.
          안 그리면 수신 확인 폴링도 함께 멈춘다. */}
      {!external && <NewSmsToastStack />}
    </div>
  );
};

const AppLayout: React.FC = () => {
  return (
    <SidebarProvider>
      <LayoutContent />
    </SidebarProvider>
  );
};

export default AppLayout;
